import { forwardRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface SectionHeaderProps {
  /** Section label — uppercase caption treatment. */
  title: ReactNode;
  /** Trailing control (button, link…) aligned to the end. */
  action?: ReactNode;
  /** Renders a muted trailing "(n)". */
  count?: number;
  /** Stick to the top of the scroll container while content scrolls under. */
  sticky?: boolean;
  className?: string;
}

/**
 * Label dividing list/screen sections. Caption-sized semibold with wide
 * tracking — the 11px overline treatment floated disconnected from its
 * group, so this carries real presence while staying a label, not a title.
 * Padding rhythm (20px above / 6px below) makes it hug the group it names.
 */
export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(
  ({ title, action, count, sticky = false, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "type-caption flex items-center justify-between gap-2 bg-surface-canvas px-4 pb-1.5 pt-5 font-semibold uppercase tracking-[0.05em] text-content-secondary",
          sticky && "sticky top-0 z-10",
          className,
        )}
      >
        <span className="flex min-w-0 items-baseline gap-1.5">
          {title}
          {typeof count === "number" ? (
            <span className="font-normal tracking-normal text-content-secondary">{`(${count})`}</span>
          ) : null}
        </span>
        {action}
      </div>
    );
  },
);
SectionHeader.displayName = "SectionHeader";
